// Helpers to turn pasted content (ChatGPT, Word, Gmail, Outlook, Notepad…) into clean plain text.
// Chat messages are stored and shown as plain text (web + mobile), so the structure
// (paragraphs, line breaks, lists, tables) is kept with characters instead of HTML.

const SKIPPED_TAGS = /^(SCRIPT|STYLE|META|LINK|TITLE|NOSCRIPT|TEMPLATE|BUTTON|SVG|IMG|IFRAME|OBJECT|INPUT|SELECT|TEXTAREA)$/;
const PARAGRAPH_TAGS = /^(P|H[1-6]|PRE|BLOCKQUOTE|TABLE|FIGURE)$/;
const BLOCK_TAGS = /^(DIV|SECTION|ARTICLE|HEADER|FOOTER|ASIDE|MAIN|NAV|ADDRESS|FIGCAPTION|DL|DT|DD|UL|OL)$/;
const LIST_INDENT = '   ';

export function htmlToPlainText(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  let out = '';
  let pendingBreaks = 0;
  let indent = '';
  let afterListMarker = false;
  let listDepth = 0;

  const requestBreaks = (count) => {
    if (out && !afterListMarker) pendingBreaks = Math.max(pendingBreaks, count);
  };

  const write = (text) => {
    if (!text) return;
    if (pendingBreaks) {
      out = out.replace(/[ \t]+$/, '') + '\n'.repeat(pendingBreaks) + indent;
      pendingBreaks = 0;
    }
    out += text;
    afterListMarker = false;
  };

  const atLineStart = () => pendingBreaks > 0 || out === '' || out.endsWith('\n' + indent) || out.endsWith(' ');

  const walkChildren = (node, pre) => node.childNodes.forEach((child) => walk(child, pre));

  const walk = (node, pre) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (pre) {
        write(node.nodeValue.replace(/\n/g, '\n' + indent));
        return;
      }
      let text = node.nodeValue.replace(/\s+/g, ' ');
      if (atLineStart()) text = text.replace(/^ /, '');
      write(text);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.nodeName.toUpperCase();
    if (SKIPPED_TAGS.test(tag)) return;

    if (tag === 'BR') {
      if (out) pendingBreaks += 1;
      return;
    }
    if (tag === 'HR') {
      requestBreaks(2);
      return;
    }
    if (tag === 'LI') {
      const parentTag = node.parentNode?.nodeName?.toUpperCase();
      let marker = listDepth > 1 ? '◦ ' : '• ';
      if (parentTag === 'OL') {
        const position = Array.from(node.parentNode.children).filter((el) => el.nodeName.toUpperCase() === 'LI').indexOf(node);
        marker = `${(parseInt(node.parentNode.getAttribute('start'), 10) || 1) + position}. `;
      }
      requestBreaks(1);
      write(marker);
      afterListMarker = true;
      const previousIndent = indent;
      indent += LIST_INDENT;
      walkChildren(node, pre);
      indent = previousIndent;
      afterListMarker = false;
      // "<li><p>…</p></li>" (ChatGPT) must not leave a blank line between items
      pendingBreaks = Math.min(pendingBreaks, 1);
      requestBreaks(1);
      return;
    }
    if (tag === 'TR') {
      requestBreaks(1);
      Array.from(node.children).forEach((cell, index) => {
        if (index > 0) write(' | ');
        walkChildren(cell, pre);
      });
      requestBreaks(1);
      return;
    }

    // Outlook / Word: one <p class="MsoNormal"> per line, empty ones are blank lines
    const isOfficeParagraph = tag === 'P' && /(^|\s)Mso/.test(node.getAttribute('class') || '');
    if (isOfficeParagraph && !node.textContent.trim()) {
      if (out) pendingBreaks += 1;
      return;
    }

    const isList = tag === 'UL' || tag === 'OL';
    const breaks = isOfficeParagraph ? 1 : PARAGRAPH_TAGS.test(tag) ? 2 : BLOCK_TAGS.test(tag) ? 1 : 0;
    // a sub-list right after its parent item's text stays attached to it
    if (isList && listDepth > 0) pendingBreaks = Math.min(pendingBreaks, 1);
    requestBreaks(breaks);
    if (isList) listDepth += 1;
    walkChildren(node, pre || tag === 'PRE');
    if (isList) listDepth -= 1;
    requestBreaks(breaks);
  };

  walkChildren(doc.body, false);
  return out;
}

// Plain text copied from ChatGPT's "Copy" button is Markdown: drop the markers the customer
// would otherwise see (**, ###, ```) and turn "- item" into "• item".
export function cleanMarkdown(text) {
  return text
    .split('\n')
    .filter((line) => !/^\s*```/.test(line))
    .map((line) =>
      line
        .replace(/^\s*(-{3,}|\*{3,}|_{3,})\s*$/, '')
        .replace(/^(\s*)#{1,6}\s+/, '$1')
        .replace(/^(\s*)[-*]\s+/, '$1• ')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/`([^`\n]+)`/g, '$1')
        .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '$1 ($2)')
    )
    .join('\n');
}

export function normalizePlainText(text) {
  return text
    .replace(/\r\n?/g, '\n')
    .replace(/[​﻿]/g, '')
    .replace(/ /g, ' ')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
}

export function clipboardToPlainText(clipboardData) {
  const html = clipboardData?.getData('text/html') || '';
  const plain = clipboardData?.getData('text/plain') || '';
  const fromHtml = html ? normalizePlainText(htmlToPlainText(html)) : '';
  return fromHtml || normalizePlainText(cleanMarkdown(plain));
}

const URL_REGEX = /(https?:\/\/[^\s<]+[^\s<.,;:!?)\]'"»])/g;

export function splitLinks(text) {
  return String(text ?? '').split(URL_REGEX);
}
